import { z } from 'zod';

/**
 * Scan Events - ephemeral Supabase Realtime broadcast contract
 *
 * Payload shapes for events emitted by the scan worker:
 * - `classification`: per-message classification result, emitted as each
 *   message is classified.
 * - `progress`: stage-aware counters (Fetch / Classify; legacy phase fields retained),
 *   emitted as the decoupled fetch + classify pipeline advances.
 *
 * Broadcast is purely ephemeral; durable scan state still lives on the
 * `scan_jobs` row.
 *
 * Source of truth: issue #281 / design artifact v20260424-1400; progress
 * event added per issue #361 (decouple fetch + classify).
 */

/** Track values match the classification pipeline's canonical casing. */
export const ScanEventTrackSchema = z.enum(['Attention', 'Archive', 'Trash']);
export type ScanEventTrack = z.infer<typeof ScanEventTrackSchema>;

/**
 * Server-side truncation limits. The worker MUST truncate to these lengths
 * before emitting (with a trailing single-character ellipsis `…` when clipped).
 * The schema uses `.max()` so any sender/subject exceeding the cap fails
 * validation in tests - we do not silently accept oversize strings.
 */
export const SCAN_EVENT_SENDER_DISPLAY_MAX = 50;
export const SCAN_EVENT_SUBJECT_HINT_MAX = 80;

export const ScanClassificationEventSchema = z.object({
  scan_job_id: z.string().uuid(),
  message_id: z.string().min(1),
  sender_display: z.string().max(SCAN_EVENT_SENDER_DISPLAY_MAX),
  subject_hint: z.string().max(SCAN_EVENT_SUBJECT_HINT_MAX),
  track: ScanEventTrackSchema,
  label: z.string().nullable(),
  classified_at: z.string().datetime({ offset: true }),
});

export type ScanClassificationEvent = z.infer<typeof ScanClassificationEventSchema>;

/**
 * Broadcast channel name, scoped to a single authenticated user.
 * Worker emits with the service role; browser subscribes with the user JWT.
 */
export function scanEventsChannelName(user_id: string): string {
  return `scan_events:${user_id}`;
}

/** Event names carried on the channel. */
export const SCAN_EVENT_CLASSIFICATION = 'classification' as const;
export const SCAN_EVENT_PROGRESS = 'progress' as const;
export type ScanEventName = typeof SCAN_EVENT_CLASSIFICATION | typeof SCAN_EVENT_PROGRESS;

/**
 * Stage-aware progress counters for the decoupled Fetch + Classify pipeline.
 *
 * - `phase_a`: retained value for Fetch in progress.
 * - `phase_b`: retained value for Classify in progress (Fetch complete).
 * - `done`: pipeline finished.
 *
 * `fetched_total` is set the moment Fetch completes; null while Fetch
 * is still running. Once non-null, it always equals `emails_fetched` at the
 * end of Fetch and never decreases.
 */
export const ScanProgressEventSchema = z.object({
  scan_job_id: z.string().uuid(),
  phase: z.enum(['phase_a', 'phase_b', 'done']),
  emails_listed: z.number().int().nonnegative(),
  emails_fetched: z.number().int().nonnegative(),
  emails_classified: z.number().int().nonnegative(),
  fetched_total: z.number().int().nonnegative().nullable(),
  /**
   * Issue #515: the LLM denominator for the determinate Classify bar.
   * Null while Fetch is still running (selection not yet frozen); set at the
   * Fetch -> Classify boundary to Σ min(classifyPerSender, volume) over the
   * selected senders, then never null again. Optional for back-compat with
   * events emitted before this field existed.
   */
  classify_total: z.number().int().nonnegative().nullable().optional(),
  /** Issue #515: number of top senders frozen into the sample (optional). */
  senders_selected: z.number().int().nonnegative().nullable().optional(),
  emitted_at: z.string().datetime({ offset: true }),
});

export type ScanProgressEvent = z.infer<typeof ScanProgressEventSchema>;

/**
 * Worker buffer rules - flush whenever either threshold is met first.
 * Also flush unconditionally on scan completion / pause / cancel / error.
 */
export const SCAN_EVENT_BUFFER_MAX_EVENTS = 10;
export const SCAN_EVENT_BUFFER_MAX_MS = 100;

/** Client-side ring-buffer + stall-detection constants. */
export const SCAN_EVENT_CLIENT_RING_BUFFER_SIZE = 50;
export const SCAN_EVENT_TICKER_VISIBLE_ROWS = 8;
export const SCAN_EVENT_STALL_THRESHOLD_MS = 5000;

/**
 * Truncate a string to `max` chars, appending `…` when clipped.
 * Kept here so backend and tests share the exact same logic.
 */
export function truncateForScanEvent(raw: string | null | undefined, max: number): string {
  if (!raw) return '';
  if (raw.length <= max) return raw;
  return raw.slice(0, Math.max(0, max - 1)) + '…';
}
